<script lang="ts">
    import BaseTab from "@/tabs/mixins/BaseTab";
    import tabIcon from "@/assets/blank.png";

    export default {
        extends: BaseTab,
        name: "Resurs",
        icon: tabIcon,
        tabName: "Resurs",
    };
</script>

<script setup lang="ts">
    import { ref, computed, toRefs } from "vue";
    import emitter from "tiny-emitter/instance";
    import KForm from "@/components/forms/Form/KForm.vue";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import { Trigger } from "@/components/forms/Form/types";
    import { Resurs } from "@/components/forms/reusables/resurserHelper";
    import { flashMessage, openDialog } from "@/utils";
    import { getErrorMessage } from "@/api";
    import { useStore } from "@/store";
    import cloneDeep from "lodash/cloneDeep";

    type ResursFormData = Resurs & {
        id?: number;
        aktiv: boolean;
    };

    const props = defineProps<{
        tabId: number;
        parameters?: ResursFormData;
    }>();

    const { tabId, parameters } = toRefs(props);

    const store = useStore();
    const formRef = ref();

    const hasUnsavedData = computed(() => formRef.value?.hasUnsavedData());

    defineExpose({ hasUnsavedData });

    const isEditing = computed(() => !!parameters?.value?.id);

    const heading = computed(() => (isEditing.value ? "Redigera resurs" : "Skapa ny resurs"));

    const resurstypOptions = [{ value: "Genomlysningsapparat", label: "Genomlysningsapparat" }];

    const initForm = async () => {
        if (parameters?.value) {
            return cloneDeep(parameters.value);
        }
        return {
            namn: "",
            totalt_antal: 1,
            ordning: 0,
            typ: null,
            tid_fore_salstid_start: null,
            tid_efter_salstid_slut: null,
            aktiv: true,
        };
    };

    const formTriggers: Trigger[] = [
        {
            fieldName: "totalt_antal",
            dependentOn: "aktiv",
            action: ({ value, dependentOnValue, initial = false }) => {
                if (!initial) {
                    if (!dependentOnValue) {
                        return { value: 0 };
                    }
                    return { value: 1 };
                }
                return { value };
            },
        },
        {
            fieldName: "aktiv",
            dependentOn: "totalt_antal",
            action: ({ dependentOnValue }) => {
                if ((dependentOnValue as number) < 1) {
                    return { value: false };
                }
                return { value: true };
            },
        },
    ];

    const onSubmit = async (data: any) => {
        const resursData = {
            ...(parameters?.value ?? { id: null, aktiv: true }),
            ...data,
        };

        let response;
        let msg: string;
        let status: number;

        try {
            if (resursData.id) {
                response = await store.dispatch("resurs/updateResurs", { resurs: resursData });
                msg = "Resursen har uppdaterats";
                status = 200;
            } else {
                response = await store.dispatch("resurs/createResurs", { resurs: resursData });
                msg = "Resursen har sparats";
                status = 201;
            }
        } catch (e) {
            openDialog("Kunde inte spara resurs: " + getErrorMessage(e), "warning");
            return;
        }

        if (response.status === status) {
            await store.dispatch("resurs/fetchResurser", { forceRefresh: true });
            await store.dispatch("tabs/closeTab", tabId.value);
            flashMessage(msg);
        }
    };

    const cancelForm = ({ hasUnsavedData }: { hasUnsavedData: boolean }) => {
        if (hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    { title: "Fortsätt redigera", type: "secondary" },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => store.dispatch("tabs/closeTab", tabId.value),
                    },
                ],
            });
        } else {
            store.dispatch("tabs/closeTab", tabId.value);
        }
    };
</script>

<template>
    <KForm
        ref="formRef"
        id="resurs"
        :initial-values-getter="initForm"
        :triggers="formTriggers"
        submit-content="Spara"
        cancel-content="Avbryt"
        @submit="onSubmit"
        @cancel="cancelForm"
    >
        <template #header>
            <admin-lock-border />
            <h2>{{ heading }}</h2>
        </template>
        <template #default>
            <div class="form-content">
                <KText name="namn" label="Namn" :maxCount="30" />
                <KNumber
                    name="totalt_antal"
                    label="Totalt antal"
                    suffixText="Om värdet sätts till 0 blir resursen inaktiv."
                />
                <KNumber name="ordning" label="Ordningsnummer" />
                <KAutocomplete name="typ" label="Resurstyp" :options="resurstypOptions" />
                <KNumber name="tid_fore_salstid_start" label="Tid före salstid start (minuter)" />
                <KNumber name="tid_efter_salstid_slut" label="Tid efter salstid slut (minuter)" />
                <KCheckbox name="aktiv" label="Aktiv" valueType="boolean" valign="center" />
            </div>
        </template>
    </KForm>
</template>

<style lang="scss" scoped></style>
